// TransactionComponent.jsx
import { useEffect } from "react";
import {
  EditableInputComponent,
  EditableSelectComponent,
  EditableTextareaComponent,
} from "components/index";
import SelectType from "components/addAcquisition/components/selectType";
import { AlertInfo, ThumbsDown, ThumbsUp } from "assets/icons";
import SearchComponent, {
  SearchMode,
} from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import { UserSelection } from "shared/components/userSelection/userSelection";
import classNames from "classnames";
import {
  AcquisitionConLabels,
  AcquisitionProLabels,
  AcquisitionSource,
  DealType,
} from "shared/types/acquisition";
import { Toggle } from "components/toggle/toggle";

const typeDealClassNames = (isActive: boolean) =>
  classNames("w-full rounded-md", {
    "bg-brick-primary text-white": isActive,
    "bg-white text-neutral-secondary-mid": !isActive,
  });

const AcquisitionTransactionDetails = ({
  updateAcquisitionFile,
  transactionDetails,
}: any) => {
  const handleFieldChange = (field: any, value: any) => {
    updateAcquisitionFile({ [field]: value });
  };

  // Handling toggle for Pro Reasons
  const handleTogglePro = (label: any) => {
    let newProReasons = [];
    if (transactionDetails.acquisitionProReasons?.includes(label)) {
      // Remove label
      newProReasons = transactionDetails.acquisitionProReasons.filter(
        (item: any) => item !== label,
      );
    } else {
      // Add label
      newProReasons = [...transactionDetails.acquisitionProReasons, label];
    }
    handleFieldChange("acquisitionProReasons", newProReasons);
  };

  // Handling toggle for Con Reasons
  const handleToggleCon = (label: any) => {
    let newConReasons = [];
    if (transactionDetails.acquisitionConReasons?.includes(label)) {
      // Remove label
      newConReasons = transactionDetails.acquisitionConReasons.filter(
        (item: any) => item !== label,
      );
    } else {
      // Add label
      newConReasons = [...transactionDetails.acquisitionConReasons, label];
    }
    handleFieldChange("acquisitionConReasons", newConReasons);
  };

  useEffect(() => {
    console.log(transactionDetails);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="text-sm">Vraagprijs excl. overdrachtskosten</label>
        <EditableInputComponent
          editMode={true}
          isLoading={false}
          label="Vul hier in"
          value={transactionDetails.grossAskingPrice || ""}
          onChange={(value) => handleFieldChange("grossAskingPrice", value)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-sm">
          Actuele huuropbrengsten (excl. gemeenschappelijke kosten en OV)
        </label>
        <EditableInputComponent
          editMode={true}
          isLoading={false}
          label="Vul hier in"
          value={transactionDetails.grossRentalIncome || ""}
          onChange={(value) => handleFieldChange("grossRentalIncome", value)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-sm">Type deal</label>
        <div className="relative flex flex-wrap gap-2 sm:flex-nowrap">
          <SelectType
            className={typeDealClassNames(
              transactionDetails.dealType === DealType.ASSET_DEAL,
            )}
            handleClick={() =>
              handleFieldChange("dealType", DealType.ASSET_DEAL)
            }
            label="Asset deal"
            icon={<AlertInfo />}
          />
          <SelectType
            className={typeDealClassNames(
              transactionDetails.dealType === DealType.SHARE_DEAL,
            )}
            handleClick={() =>
              handleFieldChange("dealType", DealType.SHARE_DEAL)
            }
            label="Share deal"
            icon={<AlertInfo />}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-sm">Interne verantwoordelijke</label>
        <div className="flex items-center gap-2">
          <UserSelection
            defaultUser={transactionDetails.yallyResponsibleUser || ""}
            onUserSelect={(user: any) =>
              handleFieldChange("yallyResponsibleUser", user)
            }
          />
        </div>
      </div>
      <>
        <div className="flex items-center gap-1 text-sm uppercase text-neutral-secondary-mid">
          <ThumbsUp className="h-[18px] w-[18px]" /> Redenen om deze transactie
          te doen
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.values(AcquisitionProLabels)
            .sort((a, b) => a.localeCompare(b))
            .map((value) => (
              <Toggle
                onClick={handleTogglePro}
                key={value}
                label={value}
                isActive={transactionDetails.acquisitionProReasons?.includes(
                  value,
                )}
              />
            ))}
        </div>
      </>
      <>
        <div className="flex items-center gap-1 text-sm uppercase text-neutral-secondary-mid">
          <ThumbsDown className="h-[18px] w-[18px]" /> Redenen om deze
          transactie niet te doen
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.values(AcquisitionConLabels)
            .sort((a, b) => a.localeCompare(b))
            .map((value) => (
              <Toggle
                onClick={handleToggleCon}
                key={value}
                label={value}
                isActive={transactionDetails.acquisitionConReasons?.includes(
                  value,
                )}
              />
            ))}
        </div>
      </>
      <div className="flex flex-col gap-2">
        <label className="text-sm">
          Bijkomende toelichting waarom deze transactie interessant is
        </label>
        <EditableTextareaComponent
          editMode={true}
          isLoading={false}
          label="Korte toelichting waarom deze transactie interessant is"
          value={transactionDetails.acquisitionDealAppreciation}
          onChange={(value) =>
            handleFieldChange("acquisitionDealAppreciation", value)
          }
        />
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-sm">Tussenpersoon</label>
        <SearchComponent
          editMode={true}
          label="Tussenpersoon"
          onSelect={(value) => handleFieldChange("intermediary", value)}
          resourceFilter={[ResourceType.COMPANY, ResourceType.CONTACT]}
          initialValue={transactionDetails.intermediary || ""}
          mode={SearchMode.SEARCH_AND_PRESENT}
        />
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-sm">Eigenaar</label>
        <SearchComponent
          editMode={true}
          label="Eigenaar"
          onSelect={(value) => handleFieldChange("owner", value)}
          resourceFilter={[ResourceType.COMPANY, ResourceType.CONTACT]}
          initialValue={transactionDetails.owner || ""}
          mode={SearchMode.SEARCH_AND_PRESENT}
        />
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-sm">Reden van verkoop</label>
        <EditableInputComponent
          editMode={true}
          isLoading={false}
          label="Reden waarom de tegenpartij wil verkopen"
          value={transactionDetails.saleReason || ""}
          onChange={(value) => handleFieldChange("saleReason", value)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-sm">Bron</label>
        <EditableSelectComponent
          label="Kies een optie"
          value={transactionDetails?.acquisitionSource || ""}
          options={Object.values(AcquisitionSource).map((value) => ({
            label: value,
            value,
          }))}
          editMode={true}
          onChange={(value) => handleFieldChange("acquisitionSource", value)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-sm">Bron URL</label>
        <EditableInputComponent
          editMode={true}
          isLoading={false}
          label="URL waar de transactie online te vinden is"
          value={transactionDetails.acquisitionSourceUrl || ""}
          onChange={(value) => handleFieldChange("acquisitionSourceUrl", value)}
        />
      </div>
    </div>
  );
};

export default AcquisitionTransactionDetails;
